import {
  createFetchClient,
  getErrorMessage as getErrorMessageCore,
  type ApiErrorResponse,
  type paths,
} from "@soulbyte/core";
import createClient from "openapi-react-query";
import { useAuth } from "@frontegg/react";
import { useEffect, useMemo, useRef } from "react";
import { toast } from "sonner";
import { FronteggError, signOut } from "../auth/auth.utils";
import i18n from "../i18n/i18n";
import { log } from "@soulbyte/core";

export type { ApiErrorResponse };

/**
 * User-facing error message with i18n fallback.
 */
export function getErrorMessage(error: unknown, fallback?: string): string {
  return getErrorMessageCore(error, fallback || i18n.t("common_error"));
}

/**
 * Shows the translated error as a toast, keyed by the message so the
 * global mutation toast and local catch blocks collapse into one toast
 * instead of stacking duplicates for the same failure.
 */
export function toastError(error: unknown) {
  const message = translateApiError(error);
  toast.error(message, { id: message });
}

/**
 * Reads the backend error code (e.g. "MALICIOUS_CONTENT") off a thrown error.
 */
export function getApiErrorCode(error: unknown): string | undefined {
  if (error instanceof FronteggError) {
    return error.errorCode;
  }
  if (error && typeof error === "object" && "error" in error) {
    return (error as ApiErrorResponse).error?.code ?? undefined;
  }
  return undefined;
}

function fronteggErrorTranslationKey(errorCode: string): string {
  return `frontegg_error_${errorCode.toLowerCase().replace(/[^a-z0-9]+/g, "_")}`;
}

/**
 * Turns a thrown API error into a user-facing message. A known error code maps
 * to its `api_error_<code>` translation; otherwise we fall back to the backend
 * message, then to the generic error text.
 */
export function translateApiError(error: unknown): string {
  if (error instanceof FronteggError) {
    if (error.errorCode) {
      const key = fronteggErrorTranslationKey(error.errorCode);
      if (i18n.exists(key)) {
        return i18n.t(key);
      }
    }
    if (error.message.trim()) {
      return error.message;
    }
  }

  const code = getApiErrorCode(error);
  if (code && !(error instanceof FronteggError)) {
    const key = `api_error_${code.toLowerCase()}`;
    if (i18n.exists(key)) {
      return i18n.t(key);
    }
  }
  return getErrorMessage(error);
}

/**
 * The authenticated low-level fetch client.
 *
 * Prefer useApiClient() — this exists for the rare mutation that needs to
 * interpret a status code itself rather than let it reject. A rejected mutation
 * always reaches the global onMutationError handler and toasts, so an endpoint
 * where a specific status is *success* (e.g. 404 on a DELETE, meaning the thing
 * is already gone) has to resolve rather than throw.
 */
export function useFetchClient() {
  const { user } = useAuth();
  const userRef = useRef(user);

  useEffect(() => {
    userRef.current = user;
  }, [user]);

  return useMemo(() => {
    // eslint-disable-next-line react-hooks/refs
    const client = createFetchClient<paths>({
      baseUrl: `${import.meta.env.VITE_API_URL}/api/v1`,
      getToken: () => userRef.current?.accessToken,
      onUnauthorized: signOut,
      onResponseError: (error: Error, context) => {
        log.error(error, {
          tags: {
            error_type: "api",
            http_status: String(context.status),
          },
          extra: {
            url: context.url,
            method: context.method,
            status: context.status,
            statusText: context.statusText,
          },
          breadcrumbs: [
            {
              level: "error",
              message: `API request failed: ${context.method} ${context.url}`,
              category: "api.error",
            },
          ],
        });
      },
    });
    return client;
  }, []);
}

/**
 * Creates an authenticated API client for making requests to the backend.
 * Uses Frontegg for token and signOut for 401; each hook instance gets fresh token via ref.
 */
export function useApiClient() {
  const fetchClient = useFetchClient();
  return useMemo(() => createClient(fetchClient), [fetchClient]);
}
